"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="4137" className="flex flex-col items-center justify-center p-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-none bg-primary/10 flex items-center justify-center mb-2 border border-primary/20">
          <div className="w-8 h-8 rounded-none bg-primary" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-primary">
          Project Initiated
        </h3>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 w-full max-w-md mx-auto">
      <div className="space-y-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-medium">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-slate-300 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-blue-500 bg-transparent placeholder:text-slate-400 h-auto py-2 text-lg font-sans text-slate-900" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-medium">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-slate-300 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-blue-500 bg-transparent placeholder:text-slate-400 h-auto py-2 text-lg font-sans text-slate-900" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-medium">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="border-0 border-b border-slate-300 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-blue-500 bg-transparent placeholder:text-slate-400 h-auto py-2 text-lg font-sans text-slate-900" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-medium">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} className="border-0 border-b border-slate-300 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-blue-500 bg-transparent placeholder:text-slate-400 h-auto py-2 text-lg font-sans text-slate-900" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        {}
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-mono font-medium">
                Project Details
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Describe your design or manufacturing requirements" {...field} className="border-0 border-b border-slate-300 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-blue-500 bg-transparent placeholder:text-slate-400 min-h-[100px] resize-y py-2 text-lg font-sans text-slate-900" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-mono rounded-none">
          {submitError}
        </div>}

      <div className="flex justify-center pt-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-none bg-[#0F172A] text-white hover:bg-blue-600 px-10 py-6 text-base font-bold uppercase tracking-widest shadow-none transition-colors duration-200">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </> : "Submit Project"}
        </Button>
      </div>
    </Form>;
}
export default function ConnectTheDots() {
  return <section id="initiate-your-project" className="relative w-full py-24 md:py-32 overflow-hidden bg-[#F1F5F9]">
      {}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none z-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="container relative z-10 px-4 mx-auto">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-[#0F172A] mb-4 tracking-tight">
            Initiate Your Project
          </h2>
          <p className="text-slate-500 text-lg font-sans">
            Bring your technical vision to life with Quinn Studio's precision engineering.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-none p-8 md:p-12 shadow-sm max-w-xl mx-auto">
          <FormProvider formKey="project_initiation_form_home" formId="project_initiation_form_home" sectionName="initiate-your-project" defaultValues={{
          name: "",
          email: "",
          company: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Submit Project" successMessage="Thank you for reaching out. A design specialist will review your specifications shortly.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}